import { openDB } from 'idb';
import { recategorizeNotaAsync } from './categorizer.js';
import { authManager } from './auth-manager.js';

const DB_NAME = 'nfce-tracker-db';
const DB_VERSION = 7;

export async function initDB() {
  try {
    return await openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        // Store for notas
        if (!db.objectStoreNames.contains('notas')) {
          const notaStore = db.createObjectStore('notas', { keyPath: 'id', autoIncrement: true });
          notaStore.createIndex('chaveAcesso', 'chaveAcesso', { unique: true });
          notaStore.createIndex('dataEmissao', 'dataEmissao');
          notaStore.createIndex('syncStatus', 'syncStatus');
        }

        // Store for pending operations queue (offline mode)
        if (!db.objectStoreNames.contains('pendingQueue')) {
          db.createObjectStore('pendingQueue', { keyPath: 'id', autoIncrement: true });
        }

        // Store for categories cache
        if (!db.objectStoreNames.contains('categorias')) {
          db.createObjectStore('categorias', { keyPath: 'id' });
        }

        // Store for global/user product category overrides (EAN -> Category/Name)
        if (!db.objectStoreNames.contains('product_dictionary')) {
          const dictStore = db.createObjectStore('product_dictionary', { keyPath: 'key' }); // key = EAN or normalized name
          dictStore.createIndex('ean', 'ean');
          dictStore.createIndex('updatedAt', 'updatedAt');
        }

        // Store for scanned products (PoC)
        if (!db.objectStoreNames.contains('produtos')) {
          const prodStore = db.createObjectStore('produtos', { keyPath: 'id' });
          prodStore.createIndex('barcode', 'barcode', { unique: false });
          prodStore.createIndex('scannedAt', 'scannedAt');
        }

        // Store for price quotes (cotacoes)
        if (!db.objectStoreNames.contains('cotacoes')) {
          const cotStore = db.createObjectStore('cotacoes', { keyPath: 'id' });
          cotStore.createIndex('ean', 'ean');
          cotStore.createIndex('mercado', 'mercado');
          cotStore.createIndex('createdAt', 'createdAt');
          cotStore.createIndex('statusEstabelecimento', 'statusEstabelecimento');
        }

        // Store for shopping lists
        if (!db.objectStoreNames.contains('listas')) {
          const listaStore = db.createObjectStore('listas', { keyPath: 'id' });
          listaStore.createIndex('scope', 'scope');
          listaStore.createIndex('status', 'status');
          listaStore.createIndex('tipo', 'tipo');
          listaStore.createIndex('mesRef', 'mesRef');
          listaStore.createIndex('createdAt', 'createdAt');
        }

        // Store for shopping list items
        if (!db.objectStoreNames.contains('lista_itens')) {
          const itemStore = db.createObjectStore('lista_itens', { keyPath: 'id' });
          itemStore.createIndex('listId', 'listId');
          itemStore.createIndex('isChecked', 'isChecked');
        }

        // Store for shopping list chat messages
        if (!db.objectStoreNames.contains('lista_mensagens')) {
          const msgStore = db.createObjectStore('lista_mensagens', { keyPath: 'id' });
          msgStore.createIndex('listId', 'listId');
          msgStore.createIndex('createdAt', 'createdAt');
        }

        // Store for shopping list to notas links (N:N reconciliation)
        if (!db.objectStoreNames.contains('shopping_list_notas')) {
          const slnStore = db.createObjectStore('shopping_list_notas', { keyPath: 'id', autoIncrement: true });
          slnStore.createIndex('shoppingListId', 'shoppingListId');
          slnStore.createIndex('notaId', 'notaId');
        }
      },
    });
  } catch (err) {
    if (err && (err.name === 'VersionError' || String(err).includes('VersionError'))) {
      console.warn('IndexedDB VersionError detectado, abrindo na versao existente do navegador:', err);
      return await openDB(DB_NAME);
    }
    throw err;
  }
}

export async function getDB() {
  return initDB();
}

export async function saveNotaLocal(nota) {
  if (!nota || (!nota.id && nota.id !== 0)) return;
  const db = await getDB();

  // Check if another entry exists with the same chaveAcesso under a different ID (e.g. temporary offline ID)
  if (nota.chaveAcesso) {
    try {
      const existingByChave = await db.getFromIndex('notas', 'chaveAcesso', nota.chaveAcesso);
      if (existingByChave && existingByChave.id !== nota.id) {
        await db.delete('notas', existingByChave.id).catch(() => {});
      }
    } catch (e) {
      console.warn('Index check warning on saveNotaLocal:', e);
    }
  }

  const existing = await db.get('notas', Number(nota.id)).catch(() => null);

  const merged = {
    ...existing,
    ...nota,
    itens: (nota.itens && nota.itens.length > 0) ? nota.itens : (existing?.itens || []),
    pagamentos: (nota.pagamentos && nota.pagamentos.length > 0) ? nota.pagamentos : (existing?.pagamentos || []),
  };

  const finalNota = await recategorizeNotaAsync(merged);
  return db.put('notas', finalNota);
}

export async function getNotasLocal() {
  const db = await getDB();
  try {
    const notas = await db.getAll('notas');
    if (notas && notas.length > 0) {
      const recategorized = await Promise.all(notas.map(n => recategorizeNotaAsync(n)));
      return recategorized.sort((a, b) => new Date(b.dataEmissao || 0) - new Date(a.dataEmissao || 0));
    }
  } catch (e) {}
  return db.getAllFromIndex('notas', 'dataEmissao').catch(() => []);
}

export async function getNotaByIdLocal(id) {
  const db = await getDB();
  const numId = Number(id);
  const searchId = (!isNaN(numId) && String(numId) === String(id).trim()) ? numId : id;
  let nota = await db.get('notas', searchId).catch(() => null);
  if (!nota && searchId !== id) {
    nota = await db.get('notas', id).catch(() => null);
  }

  if (!nota || !nota.itens || nota.itens.length === 0) {
    try {
      const token = authManager.getToken();
      const res = await fetch(`/api/notas/${id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const fullNota = await res.json();
        if (fullNota && fullNota.itens) {
          const recat = await recategorizeNotaAsync(fullNota);
          await db.put('notas', recat);
          return recat;
        }
      }
    } catch (e) {
      console.warn('Could not fetch full note from API:', e);
    }
  }

  return nota ? await recategorizeNotaAsync(nota) : null;
}

export async function deleteNotaLocal(id) {
  const db = await getDB();
  const numId = Number(id);
  if (!isNaN(numId)) {
    try { await db.delete('notas', numId); } catch (e) {}
  }
  try { await db.delete('notas', String(id)); } catch (e) {}
}

export async function clearAndReplaceNotasLocal(remoteNotas) {
  const db = await getDB();
  const tx = db.transaction('notas', 'readwrite');
  await tx.store.clear();
  for (const nota of remoteNotas) {
    await tx.store.put(nota);
  }
  await tx.done;
}

export async function saveCategoriesLocal(categories) {
  const db = await getDB();
  const tx = db.transaction('categorias', 'readwrite');
  for (const cat of categories) {
    await tx.store.put(cat);
  }
  await tx.done;
}

export async function getCategoriesLocal() {
  const db = await getDB();
  return db.getAll('categorias');
}

export async function addPendingOperation(op) {
  if (!op || !op.url) return;
  const db = await getDB();
  try {
    const existingQueue = await db.getAll('pendingQueue').catch(() => []);
    const alreadyQueued = existingQueue.some(item => item.url === op.url && item.type === op.type);
    if (alreadyQueued) {
      return;
    }
    return db.add('pendingQueue', {
      ...op,
      retries: op.retries || 0,
      createdAt: op.createdAt || new Date().toISOString()
    });
  } catch (e) {
    console.warn('addPendingOperation error:', e);
  }
}

export async function getPendingOperations() {
  const db = await getDB();
  return db.getAll('pendingQueue');
}

export async function removePendingOperation(id) {
  const db = await getDB();
  return db.delete('pendingQueue', id);
}

export async function sanitizePendingQueue() {
  try {
    const db = await getDB();
    const queue = await db.getAll('pendingQueue').catch(() => []);
    if (!queue || queue.length === 0) return;

    const seen = new Set();
    const toKeep = [];
    const toDelete = [];

    for (const op of queue) {
      const key = `${op.type || 'PARSE_NFCE'}_${op.url || ''}`;
      if (!op.url || seen.has(key) || (op.retries && op.retries >= 3)) {
        toDelete.push(op.id);
      } else {
        seen.add(key);
        toKeep.push(op);
      }
    }

    // Limit active queue to top 10 items max to prevent resource overload
    if (toKeep.length > 10) {
      const excess = toKeep.slice(10);
      for (const ex of excess) {
        toDelete.push(ex.id);
      }
    }

    for (const id of toDelete) {
      await db.delete('pendingQueue', id).catch(() => {});
    }
  } catch (e) {
    console.warn('Failed to sanitize pending queue:', e);
  }
}

/**
 * Saves or updates a scanned product into the master catalog in IndexedDB
 * Uses EAN as unique key, preserving price history and enrichment status
 */
export async function saveProdutoScanned(produto) {
  if (!produto) return null;
  const db = await getDB();
  const barcode = (produto.barcode || produto.ean || '').trim();
  const id = barcode || produto.id || `PROD_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;

  let existing = null;
  if (barcode) {
    existing = await db.get('produtos', barcode).catch(() => null);
  }
  if (!existing && produto.id) {
    existing = await db.get('produtos', produto.id).catch(() => null);
  }

  const rawPrice = produto.rawPrecoGondola !== undefined 
    ? produto.rawPrecoGondola 
    : (produto.preco !== undefined ? produto.preco : (existing?.rawPrecoGondola || null));

  const precoGondola = produto.precoGondola !== undefined 
    ? produto.precoGondola 
    : (typeof rawPrice === 'number' && rawPrice > 0 
        ? `R$ ${rawPrice.toFixed(2).replace('.', ',')}` 
        : (existing?.precoGondola || null));

  // Determine enrichment status
  const rawDesc = (produto.descricao || existing?.descricao || '').trim();
  const isGenericDesc = !rawDesc || rawDesc === 'Produto sem descrição' || rawDesc === 'Produto sem descrição OCR' || rawDesc === 'Identificando...';
  const status = produto.status || (isGenericDesc ? 'pendente' : 'enriquecido');
  const descricao = isGenericDesc ? (existing?.descricao || 'Produto sem descrição') : rawDesc;

  const isEnrichOnly = Boolean(produto.isEnrichOnly);

  // Track price history (up to 20 recent price points) - only add new entries if not an enrich-only update
  const historicoPrecos = Array.isArray(existing?.historicoPrecos) ? [...existing.historicoPrecos] : [];
  if (!isEnrichOnly && typeof rawPrice === 'number' && rawPrice > 0) {
    const lastPriceEntry = historicoPrecos[historicoPrecos.length - 1];
    if (!lastPriceEntry || lastPriceEntry.preco !== rawPrice) {
      historicoPrecos.push({
        preco: rawPrice,
        data: new Date().toISOString(),
        local: produto.ultimoMercado || existing?.ultimoMercado || 'Gôndola'
      });
      if (historicoPrecos.length > 20) {
        historicoPrecos.shift();
      }
    }
  }

  const scanCount = isEnrichOnly ? (existing?.scanCount || 1) : ((existing?.scanCount || 0) + 1);
  const updatedRecord = {
    id: barcode || existing?.id || id,
    barcode: barcode || existing?.barcode || '',
    ean: barcode || existing?.ean || '',
    format: produto.format || existing?.format || 'EAN_13',
    descricao,
    descricao_bruta: produto.descricao_bruta || existing?.descricao_bruta || '',
    categoria: produto.categoria || produto.category_name || existing?.categoria || 'Outros',
    marca: produto.marca || existing?.marca || '',
    preco: rawPrice,
    precoGondola,
    rawPrecoGondola: rawPrice,
    preco_sugerido: produto.preco_sugerido !== undefined ? produto.preco_sugerido : (existing?.preco_sugerido || false),
    peso_ou_quantidade: produto.peso_ou_quantidade || existing?.peso_ou_quantidade || '',
    ultimoPrecoPago: produto.ultimoPrecoPago !== undefined ? produto.ultimoPrecoPago : (existing?.ultimoPrecoPago || null),
    ultimoMercado: produto.ultimoMercado || existing?.ultimoMercado || '',
    ultimaDataCompra: produto.ultimaDataCompra || existing?.ultimaDataCompra || '',
    diferencaPreco: produto.diferencaPreco !== undefined ? produto.diferencaPreco : (existing?.diferencaPreco || null),
    diferencaPercent: produto.diferencaPercent !== undefined ? produto.diferencaPercent : (existing?.diferencaPercent || null),
    status,
    source: produto.source || existing?.source || 'manual',
    scannedAt: new Date().toISOString(),
    scanCount,
    historicoPrecos
  };

  await db.put('produtos', updatedRecord);

  // Sync to Backend if authenticated
  if (authManager.isAuthenticated()) {
    try {
      const numPrice = typeof updatedRecord.rawPrecoGondola === 'number'
        ? updatedRecord.rawPrecoGondola
        : (typeof updatedRecord.preco === 'number' ? updatedRecord.preco : (parseFloat(String(updatedRecord.precoGondola || '').replace(/[^\d,]/g, '').replace(',', '.')) || 0));

      authManager.fetchWithAuth('/api/produtos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ean: updatedRecord.ean || updatedRecord.barcode || updatedRecord.id,
          barcode: updatedRecord.barcode || updatedRecord.ean || '',
          descricao: updatedRecord.descricao,
          descricao_bruta: updatedRecord.descricao_bruta || '',
          categoria: updatedRecord.categoria || 'Outros',
          marca: updatedRecord.marca || '',
          peso_ou_quantidade: updatedRecord.peso_ou_quantidade || '',
          preco: numPrice,
          precoGondola: numPrice,
          preco_sugerido: Boolean(updatedRecord.preco_sugerido),
          status: updatedRecord.status || 'pendente'
        })
      }).catch(err => console.warn('[saveProdutoScanned] API sync warning:', err));
    } catch (e) {
      console.warn('[saveProdutoScanned] Sync failed:', e);
    }
  }

  window.dispatchEvent(new CustomEvent('produtosscannedupdated', { detail: updatedRecord }));
  return updatedRecord;
}

/**
 * Retrieves all scanned products from IndexedDB & synchronizes with backend master catalog
 */
export async function getProdutosScanned() {
  const db = await getDB();
  const localMap = new Map();

  // 1. Load local cache
  try {
    const localProds = await db.getAll('produtos');
    for (const p of (localProds || [])) {
      localMap.set(p.ean || p.barcode || String(p.id), p);
    }
  } catch (e) {
    console.warn('getProdutosScanned local error:', e);
  }

  // 2. Fetch from Backend Master Catalog & Upstream Local-Only Products
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth('/api/produtos');
      if (res.ok) {
        const data = await res.json();
        const serverProds = data.produtos || [];
        const serverKeys = new Set();

        for (const sp of serverProds) {
          const key = sp.ean || sp.barcode;
          if (!key) continue;
          serverKeys.add(key);

          const existingLocal = localMap.get(key);
          const merged = {
            id: key,
            barcode: key,
            ean: key,
            format: 'EAN_13',
            descricao: sp.descricao,
            descricao_bruta: sp.descricao_bruta || '',
            categoria: sp.categoria || 'Outros',
            marca: sp.marca || '',
            preco: sp.preco || 0,
            precoGondola: sp.preco || 0,
            preco_sugerido: Boolean(sp.preco_sugerido),
            peso_ou_quantidade: sp.peso_ou_quantidade || '',
            status: sp.status || 'pendente',
            scannedAt: sp.updated_at || sp.created_at || (existingLocal ? existingLocal.scannedAt : new Date().toISOString()),
            scanCount: sp.scan_count || (existingLocal ? existingLocal.scanCount : 1),
            historicoPrecos: existingLocal ? (existingLocal.historicoPrecos || []) : []
          };

          localMap.set(key, merged);
          await db.put('produtos', merged).catch(() => {});
        }

        // 3. UPSTREAM: Upload any local products that are not yet on the server!
        for (const [key, lp] of localMap.entries()) {
          if (!serverKeys.has(key)) {
            const lpPrice = typeof lp.rawPrecoGondola === 'number'
              ? lp.rawPrecoGondola
              : (typeof lp.preco === 'number' ? lp.preco : (parseFloat(String(lp.precoGondola || '').replace(/[^\d,]/g, '').replace(',', '.')) || 0));

            authManager.fetchWithAuth('/api/produtos', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ean: lp.ean || lp.barcode || lp.id || key,
                barcode: lp.barcode || lp.ean || key,
                descricao: lp.descricao,
                descricao_bruta: lp.descricao_bruta || '',
                categoria: lp.categoria || 'Outros',
                marca: lp.marca || '',
                peso_ou_quantidade: lp.peso_ou_quantidade || '',
                preco: lpPrice,
                precoGondola: lpPrice,
                preco_sugerido: Boolean(lp.preco_sugerido),
                status: lp.status || 'pendente'
              })
            }).catch(e => console.warn('[getProdutosScanned] Upstream sync warning:', e));
          }
        }
      }
    } catch (e) {
      console.warn('[getProdutosScanned] Backend fetch warning:', e);
    }
  }

  return Array.from(localMap.values()).sort((a, b) => new Date(b.scannedAt || 0) - new Date(a.scannedAt || 0));
}

/**
 * Deletes a scanned product from IndexedDB and Backend
 */
export async function deleteProdutoScanned(id) {
  const db = await getDB();
  const key = String(id || '').trim();
  if (!key) return;

  // 1. Delete by direct key
  await db.delete('produtos', key).catch(() => {});

  // 2. Also purge any record in local store matching id, ean, or barcode
  try {
    const all = await db.getAll('produtos');
    for (const item of (all || [])) {
      if (String(item.id) === key || String(item.ean) === key || String(item.barcode) === key) {
        await db.delete('produtos', item.id).catch(() => {});
      }
    }
  } catch (err) {
    console.warn('[deleteProdutoScanned] local search/purge warning:', err);
  }

  // 3. Await Backend Master Catalog deletion
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/produtos/${encodeURIComponent(key)}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        console.warn('[deleteProdutoScanned] Backend delete returned non-2xx status:', res.status);
      }
    } catch (e) {
      console.warn('[deleteProdutoScanned] Backend delete error:', e);
    }
  }

  window.dispatchEvent(new CustomEvent('produtosscannedupdated'));
}

/**
 * Calculates summary statistics for the products catalog
 */
export async function getProdutosStats() {
  const prods = await getProdutosScanned();
  const total = prods.length;
  const enriquecidos = prods.filter(p => p.status === 'enriquecido').length;
  const pendentes = total - enriquecidos;
  return { total, enriquecidos, pendentes };
}

/**
 * Validates whether a barcode string is a valid commercial GS1 GTIN/EAN (EAN-8, UPC-12, EAN-13, GTIN-14)
 */
export function isValidEAN(code) {
  if (!code || typeof code !== 'string') return false;
  const clean = code.trim();
  if (!/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(clean)) {
    return false;
  }
  const len = clean.length;
  const checkDigit = parseInt(clean[len - 1], 10);
  let sum = 0;
  for (let i = 0; i < len - 1; i++) {
    const digit = parseInt(clean[i], 10);
    const weight = ((len - 1 - i) % 2 === 1) ? 3 : 1;
    sum += digit * weight;
  }
  const calculatedCheck = (10 - (sum % 10)) % 10;
  return checkDigit === calculatedCheck;
}

/**
 * Enriches a single product using local catalog quotes, local NFC-e history and Gemini AI
 */
export async function enrichSingleProduto(prod) {
  if (!prod) return null;
  const ean = (prod.ean || prod.barcode || '').trim();
  const rawDesc = (prod.descricao_bruta || prod.descricao || '').trim();

  // 1. Validar se o EAN é um código de barras comercial válido
  const hasValidEAN = isValidEAN(ean);
  if (!hasValidEAN && !rawDesc) {
    throw new Error('Código de barras inválido e sem descrição suficiente para identificar.');
  }

  // 2. Verificar se já existe cotação/produto já enriquecido no catálogo local para o mesmo EAN
  const db = await getDB();
  const allProds = await db.getAll('produtos').catch(() => []);
  const existingEnriched = allProds.find(p => 
    String(p.id) !== String(prod.id) && 
    (String(p.ean).trim() === ean || String(p.barcode).trim() === ean) && 
    p.status === 'enriquecido' && 
    p.descricao && 
    p.descricao !== 'Produto sem descrição' &&
    !p.descricao.toLowerCase().includes('vl. total')
  );

  if (existingEnriched) {
    return await saveProdutoScanned({
      ...prod,
      descricao: existingEnriched.descricao,
      categoria: existingEnriched.categoria || prod.categoria || 'Outros',
      marca: existingEnriched.marca || prod.marca || '',
      peso_ou_quantidade: existingEnriched.peso_ou_quantidade || prod.peso_ou_quantidade || '',
      status: 'enriquecido',
      isEnrichOnly: true
    });
  }

  // 3. Verificar nas notas fiscais locais se existe o item com descrição detalhada
  const notas = await getNotasLocal();
  let foundDesc = '';
  let foundCat = '';

  for (const n of notas) {
    if (Array.isArray(n.itens)) {
      const item = n.itens.find(it => String(it.codigo || it.codigoProduto || '').trim() === ean);
      if (item && item.descricao) {
        const itemDesc = item.descricao.trim();
        const isClean = !itemDesc.toLowerCase().includes('vl. total') && !itemDesc.toLowerCase().includes('v. total') && itemDesc.length > 3;
        if (isClean) {
          foundDesc = itemDesc;
          foundCat = item.categoria || item.category_name || '';
          break;
        }
      }
    }
  }

  if (foundDesc) {
    return await saveProdutoScanned({
      ...prod,
      descricao: foundDesc,
      categoria: foundCat || prod.categoria || 'Outros',
      status: 'enriquecido',
      isEnrichOnly: true
    });
  }

  // 4. Query Gemini AI Barcode Enrichment API
  const headers = {
    'Content-Type': 'application/json',
    'X-User-Id': (authManager && typeof authManager.getUserId === 'function' ? authManager.getUserId() : '') || ''
  };
  const token = authManager && typeof authManager.getToken === 'function' ? authManager.getToken() : null;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const resp = await fetch('/api/scanner/enrich-barcode', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ean: ean,
      raw_desc: rawDesc,
      is_valid_ean: hasValidEAN
    })
  });

  if (!resp.ok) {
    const errData = await resp.json().catch(() => ({}));
    throw new Error(errData.error || `Erro HTTP ${resp.status}`);
  }

  const aiData = await resp.json();
  if (aiData && aiData.descricao) {
    const canonicalEAN = (aiData.ean_canonico || '').trim();

    // Reconciliação / Mesclagem com EAN Canônico Oficial GS1
    if (canonicalEAN && isValidEAN(canonicalEAN) && canonicalEAN !== ean) {
      const canonicalProd = allProds.find(p => 
        String(p.id) === canonicalEAN || 
        String(p.ean).trim() === canonicalEAN || 
        String(p.barcode).trim() === canonicalEAN
      );

      if (canonicalProd) {
        // Mesclar histórico de preços de ambos sem duplicar
        const combinedHistory = [
          ...(canonicalProd.historicoPrecos || []),
          ...(prod.historicoPrecos || [])
        ];
        const uniqueHistory = [];
        const seenKeys = new Set();
        for (const h of combinedHistory) {
          const key = `${h.preco}_${h.local || ''}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueHistory.push(h);
          }
        }

        // Atualizar o registro canônico oficial com os dados limpos e o histórico unificado
        const updatedCanonical = await saveProdutoScanned({
          ...canonicalProd,
          descricao: aiData.descricao || canonicalProd.descricao,
          categoria: aiData.categoria || canonicalProd.categoria || 'Outros',
          marca: aiData.marca || canonicalProd.marca || '',
          peso_ou_quantidade: aiData.peso_ou_quantidade || canonicalProd.peso_ou_quantidade || '',
          status: 'enriquecido',
          historicoPrecos: uniqueHistory,
          isEnrichOnly: true
        });

        // Excluir o registro temporário / código interno duplicado
        await deleteProdutoScanned(prod.id || prod.ean);

        return updatedCanonical;
      } else {
        // Promover este registro para o EAN Canônico oficial
        await deleteProdutoScanned(prod.id || prod.ean);

        return await saveProdutoScanned({
          ...prod,
          id: canonicalEAN,
          ean: canonicalEAN,
          barcode: canonicalEAN,
          descricao: aiData.descricao,
          categoria: aiData.categoria || prod.categoria || 'Outros',
          marca: aiData.marca || prod.marca || '',
          peso_ou_quantidade: aiData.peso_ou_quantidade || prod.peso_ou_quantidade || '',
          status: 'enriquecido',
          isEnrichOnly: true
        });
      }
    }

    return await saveProdutoScanned({
      ...prod,
      descricao: aiData.descricao,
      categoria: aiData.categoria || prod.categoria || 'Outros',
      marca: aiData.marca || prod.marca || '',
      peso_ou_quantidade: aiData.peso_ou_quantidade || prod.peso_ou_quantidade || '',
      status: 'enriquecido',
      isEnrichOnly: true
    });
  }

  return prod;
}

/**
 * Enriches pending products asynchronously using local NFC-e history and Gemini AI
 */
export async function enrichPendingProdutos(forceAll = false) {
  const prods = await getProdutosScanned();
  const targets = prods.filter(p => forceAll || p.status === 'pendente' || !p.descricao || p.descricao === 'Produto sem descrição');
  if (targets.length === 0) return { enriched: 0, total: 0 };

  let enrichedCount = 0;
  for (const prod of targets) {
    try {
      const res = await enrichSingleProduto(prod);
      if (res && res.status === 'enriquecido') {
        enrichedCount++;
      }
    } catch (err) {
      console.warn('[db.js] Failed to enrich product with Gemini:', prod.ean, err);
    }
  }

  return { enriched: enrichedCount, total: targets.length };
}

// -------------------------------------------------------------
// Shopping Lists Local Operations
// -------------------------------------------------------------

export async function getListasLocal(scope = 'personal') {
  const db = await getDB();
  try {
    const all = await db.getAll('listas');
    const filtered = (all || []).filter(l => {
      if (scope === 'family') return l.scope === 'family';
      return l.scope === 'personal' || !l.scope;
    });
    return filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  } catch (e) {
    console.warn('getListasLocal error:', e);
    return [];
  }
}

/**
 * Loads all active/open shopping lists from server and local cache
 */
export async function getOpenShoppingLists() {
  const openListsMap = new Map();

  if (authManager.isAuthenticated()) {
    try {
      const [resPersonal, resFamily] = await Promise.all([
        authManager.fetchWithAuth('/api/listas?scope=personal').catch(() => null),
        authManager.fetchWithAuth('/api/listas?scope=family').catch(() => null),
      ]);

      if (resPersonal && resPersonal.ok) {
        const dataP = await resPersonal.json();
        for (const l of (dataP || [])) {
          await saveListaLocal(l).catch(() => {});
          if (l.status !== 'fechada' && l.status !== 'concluida' && l.status !== 'closed') {
            openListsMap.set(l.id, l);
          }
        }
      }
      if (resFamily && resFamily.ok) {
        const dataF = await resFamily.json();
        for (const l of (dataF || [])) {
          await saveListaLocal(l).catch(() => {});
          if (l.status !== 'fechada' && l.status !== 'concluida' && l.status !== 'closed') {
            openListsMap.set(l.id, l);
          }
        }
      }
    } catch (e) {
      console.warn('[getOpenShoppingLists] API fetch error:', e);
    }
  }

  // Fallback to local IndexedDB
  const db = await getDB();
  try {
    const all = await db.getAll('listas');
    for (const l of (all || [])) {
      if (l.status !== 'fechada' && l.status !== 'concluida' && l.status !== 'closed') {
        if (!openListsMap.has(l.id)) {
          openListsMap.set(l.id, l);
        }
      }
    }
  } catch (e) {
    console.warn('[getOpenShoppingLists] local db error:', e);
  }

  return Array.from(openListsMap.values()).sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
}

/**
 * Creates a quick shopping list when no lists are open
 */
export async function createQuickShoppingList(title = 'Minhas Compras', scope = 'personal') {
  const payload = {
    name: title,
    title,
    nome: title,
    scope,
    status: 'aberta',
    type: 'semanal',
    tipo: 'semanal',
    createdAt: new Date().toISOString()
  };

  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth('/api/listas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const created = await res.json();
        await saveListaLocal(created);
        return created;
      }
    } catch (e) {
      console.warn('[createQuickShoppingList] API create error:', e);
    }
  }

  const localId = Date.now();
  const localList = { id: localId, ...payload };
  await saveListaLocal(localList);
  return localList;
}

export async function getListaByIdLocal(id) {
  const db = await getDB();
  try {
    const lista = await db.get('listas', id);
    if (!lista) return null;

    // Get items
    const allItems = await db.getAllFromIndex('lista_itens', 'listId', id).catch(() => []);
    lista.items = (allItems || []).sort((a, b) => (a.isChecked ? 1 : 0) - (b.isChecked ? 1 : 0));
    lista.itemCount = lista.items.length;
    lista.checkedCount = lista.items.filter(i => i.isChecked).length;

    // Get messages
    const allMsgs = await db.getAllFromIndex('lista_mensagens', 'listId', id).catch(() => []);
    lista.messages = (allMsgs || []).sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    lista.messageCount = lista.messages.length;

    return lista;
  } catch (e) {
    console.warn('getListaByIdLocal error:', e);
    return null;
  }
}

export async function saveListaLocal(lista) {
  const id = lista.id || Date.now();
  const now = new Date().toISOString();
  const scope = lista.scope || 'personal';
  const type = lista.type || lista.tipo || 'semanal';
  const status = lista.status || 'aberta';
  const monthRef = lista.monthRef || lista.mesRef || now.slice(0, 7);
  const totalEstimated = Number(lista.totalEstimated ?? lista.totalEstimado ?? 0);
  const totalActual = Number(lista.totalActual ?? lista.totalReal ?? 0);

  const record = {
    ...lista,
    id,
    scope,
    type,
    tipo: type,
    status,
    monthRef,
    mesRef: monthRef,
    totalEstimated,
    totalEstimado: totalEstimated,
    totalActual,
    totalReal: totalActual,
    createdAt: lista.createdAt || now,
    updatedAt: now,
  };

  try {
    const db = await getDB();
    if (db.objectStoreNames.contains('listas')) {
      await db.put('listas', record);
    }
  } catch (e) {
    console.warn('Local saveListaLocal warning:', e);
  }

  return record;
}

export async function deleteListaLocal(id) {
  const db = await getDB();
  await db.delete('listas', id);
  // Delete associated items and messages
  try {
    const items = await db.getAllFromIndex('lista_itens', 'listId', id);
    for (const it of items) {
      await db.delete('lista_itens', it.id);
    }
    const msgs = await db.getAllFromIndex('lista_mensagens', 'listId', id);
    for (const m of msgs) {
      await db.delete('lista_mensagens', m.id);
    }
  } catch (e) {
    console.warn('deleteListaLocal cleanup error:', e);
  }
}

export async function closeListaLocal(id) {
  const db = await getDB();
  const lista = await db.get('listas', id);
  if (!lista) return;
  lista.status = 'fechada';
  lista.closedAt = new Date().toISOString();
  lista.updatedAt = new Date().toISOString();
  await db.put('listas', lista);
  return lista;
}

export async function saveListaItemLocal(item) {
  const db = await getDB();
  const id = item.id || Date.now();
  const now = new Date().toISOString();
  const record = {
    ...item,
    id,
    listId: item.listId,
    description: item.description || 'Produto',
    categoryName: item.categoryName || 'Outros',
    quantity: Number(item.quantity) || 1,
    actualQuantity: Number(item.actualQuantity || item.quantidadeComprada || 0),
    unit: item.unit || 'UN',
    estimatedPrice: Number(item.estimatedPrice) || 0,
    actualPrice: Number(item.actualPrice) || 0,
    isChecked: Boolean(item.isChecked),
    barcode: item.barcode || '',
    addedBy: item.addedBy || 'Usuário',
    createdAt: item.createdAt || now,
    updatedAt: now,
  };
  await db.put('lista_itens', record);
  await recalculateListaTotalsLocal(item.listId);
  return record;
}

export async function deleteListaItemLocal(itemId, listId) {
  const db = await getDB();
  await db.delete('lista_itens', itemId);
  if (listId) {
    await recalculateListaTotalsLocal(listId);
  }
}

export async function toggleListaItemLocal(itemId, listId, isChecked, actualQuantity, actualPrice) {
  const db = await getDB();
  const it = await db.get('lista_itens', itemId);
  if (it) {
    it.isChecked = isChecked;
    if (actualQuantity !== undefined) {
      it.actualQuantity = Number(actualQuantity);
    }
    if (actualPrice !== undefined) {
      it.actualPrice = Number(actualPrice);
    }
    it.updatedAt = new Date().toISOString();
    await db.put('lista_itens', it);
    await recalculateListaTotalsLocal(listId);
  }
}

export async function recalculateListaTotalsLocal(listId) {
  const db = await getDB();
  try {
    const lista = await db.get('listas', listId);
    if (!lista) return;
    const items = await db.getAllFromIndex('lista_itens', 'listId', listId).catch(() => []);
    let est = 0;
    let act = 0;
    items.forEach(it => {
      const q = it.quantity || 1;
      const actQ = it.actualQuantity > 0 ? it.actualQuantity : q;
      est += q * (it.estimatedPrice || 0);
      if (it.isChecked) {
        act += actQ * (it.actualPrice > 0 ? it.actualPrice : (it.estimatedPrice || 0));
      }
    });
    lista.totalEstimado = est;
    lista.totalReal = act;
    lista.totalEstimated = est;
    lista.totalActual = act;
    lista.updatedAt = new Date().toISOString();
    await db.put('listas', lista);
  } catch (e) {
    console.warn('recalculateListaTotalsLocal error:', e);
  }
}

export async function saveListaMensagemLocal(msg) {
  const db = await getDB();
  const id = msg.id || Date.now();
  const record = {
    ...msg,
    id,
    listId: msg.listId,
    userId: msg.userId || 'usr_local',
    userName: msg.userName || 'Usuário',
    userPicture: msg.userPicture || '',
    message: msg.message || '',
    createdAt: msg.createdAt || new Date().toISOString(),
  };
  await db.put('lista_mensagens', record);
  return record;
}

/**
 * Searches historical products across all local NFC-e notas
 */
export function cleanProductDescription(raw) {
  if (!raw) return '';
  return raw
    .replace(/Vl\.\s*Total[\s\d.,R$]*/gi, '')
    .replace(/Valor\s*Total[\s\d.,R$]*/gi, '')
    .replace(/Qtde[\s\d.,]*[A-Z]*/gi, '')
    .replace(/UN:\s*[A-Z]+/gi, '')
    .replace(/Vl\.\s*Unit[\s\d.,R$]*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export async function searchHistoricalProducts(query) {
  if (!query || query.trim().length < 2) return [];
  const normalizedQ = query.trim().toLowerCase();
  const notas = await getNotasLocal();
  const foundMap = {};

  notas.forEach(n => {
    (n.itens || []).forEach(i => {
      const rawDesc = (i.descricao || '').trim();
      const desc = cleanProductDescription(rawDesc) || rawDesc;
      if (desc.toLowerCase().includes(normalizedQ)) {
        const key = desc.toLowerCase().replace(/[^a-z0-9]/g, '');
        const unitVal = (i.quantidade > 0 && i.valorTotal) ? (i.valorTotal / i.quantidade) : (i.valorUnitario || 0);
        const dateMs = n.dataEmissao ? new Date(n.dataEmissao).getTime() : 0;

        if (!foundMap[key] || dateMs > foundMap[key].dateMs) {
          foundMap[key] = {
            description: desc,
            categoryName: i.categoriaNome || 'Outros',
            unit: i.unidade || 'UN',
            estimatedPrice: unitVal,
            store: n.emitente?.razaoSocial || n.razaoSocial || '',
            dateMs
          };
        }
      }
    });
  });

  return Object.values(foundMap).slice(0, 8);
}

export async function saveShoppingListNotaLinkLocal(link) {
  const db = await getDB();
  const id = link.id || `${link.shoppingListId}_${link.notaId}`;
  const record = {
    ...link,
    id,
    shoppingListId: Number(link.shoppingListId),
    notaId: Number(link.notaId),
    matchedVia: link.matchedVia || 'temporal_suggestion',
    confidenceScore: link.confidenceScore || 1.0,
    allocatedAmount: link.allocatedAmount || null,
    createdAt: link.createdAt || new Date().toISOString(),
  };
  if (db.objectStoreNames.contains('shopping_list_notas')) {
    await db.put('shopping_list_notas', record);
  }
  return record;
}

export async function deleteShoppingListNotaLinkLocal(shoppingListId, notaId) {
  const db = await getDB();
  if (db.objectStoreNames.contains('shopping_list_notas')) {
    const all = await db.getAllFromIndex('shopping_list_notas', 'shoppingListId', Number(shoppingListId)).catch(() => []);
    for (const item of all) {
      if (Number(item.notaId) === Number(notaId)) {
        await db.delete('shopping_list_notas', item.id);
      }
    }
  }
}

export async function getShoppingListNotasLocal(shoppingListId) {
  const db = await getDB();
  if (!db.objectStoreNames.contains('shopping_list_notas')) return [];
  return await db.getAllFromIndex('shopping_list_notas', 'shoppingListId', Number(shoppingListId)).catch(() => []);
}

export async function getNotasShoppingListsLocal(notaId) {
  const db = await getDB();
  if (!db.objectStoreNames.contains('shopping_list_notas')) return [];
  return await db.getAllFromIndex('shopping_list_notas', 'notaId', Number(notaId)).catch(() => []);
}

/**
 * Extracts top frequent store names from saved NFC-e receipts
 */
export async function getFrequentStoresFromNotas() {
  const notas = await getNotasLocal();
  const storeCounts = new Map();
  for (const n of (notas || [])) {
    const name = (n.emitente?.razaoSocial || n.emitente?.nomeFantasia || '').trim();
    if (name && name.length >= 3) {
      storeCounts.set(name, (storeCounts.get(name) || 0) + 1);
    }
  }
  return Array.from(storeCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(e => e[0]);
}

/**
 * Saves a price quote locally and synchronizes to the backend
 */
export async function saveCotacaoLocal(cotacao) {
  const db = await getDB();
  const id = cotacao.id || `cot_${Date.now()}_${cotacao.ean || 'item'}`;
  const now = new Date().toISOString();
  const rawPrice = typeof cotacao.preco === 'number'
    ? cotacao.preco
    : (parseFloat(String(cotacao.preco || '').replace(/[^\d,]/g, '').replace(',', '.')) || 0);

  const record = {
    id,
    ean: cotacao.ean || '',
    descricao: cotacao.descricao || '',
    mercado: cotacao.mercado || 'Não informado',
    preco: rawPrice,
    precoOriginal: cotacao.precoOriginal || null,
    tipoFonte: cotacao.tipoFonte || 'scan_gondola',
    statusEstabelecimento: cotacao.statusEstabelecimento || (cotacao.mercado && cotacao.mercado !== 'Não informado' ? 'definido' : 'pendente'),
    createdAt: cotacao.createdAt || now,
    userName: cotacao.userName || '',
    cidadeUF: cotacao.cidadeUF || ''
  };

  if (db.objectStoreNames.contains('cotacoes')) {
    try {
      await db.put('cotacoes', record);
    } catch (e) {
      console.warn('saveCotacaoLocal IndexedDB warning:', e);
    }
  }

  if (authManager.isAuthenticated()) {
    try {
      authManager.fetchWithAuth('/api/cotacoes/registrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: record.id,
          ean: record.ean,
          descricao: record.descricao,
          mercado: record.mercado,
          preco: record.preco,
          preco_original: record.precoOriginal,
          tipo_fonte: record.tipoFonte,
          status_estabelecimento: record.statusEstabelecimento,
          user_name: record.userName,
          cidade_uf: record.cidadeUF
        })
      }).catch(err => console.warn('[saveCotacaoLocal] API sync warning:', err));
    } catch (e) {
      console.warn('[saveCotacaoLocal] Sync error:', e);
    }
  }

  window.dispatchEvent(new CustomEvent('cotacaosaved', { detail: record }));
  return record;
}

/**
 * Retrieves quotes for an EAN product
 */
/**
 * Retrieves quotes for an EAN product
 */
/**
 * Retrieves quotes for an EAN product (IndexedDB-first, fast local cache)
 */
export async function getCotacoesByEAN(ean, { fetchRemote = false } = {}) {
  if (!ean) return [];
  const cleanEan = String(ean).trim();
  const results = [];
  const seenIds = new Set();
  const seenQuotes = new Set(); // deduplicate (mercado + preco + dateMinute)

  // 1. Fetch from IndexedDB 'cotacoes' store first (instant)
  if (typeof indexedDB !== 'undefined') {
    try {
      const db = await getDB();
      if (db.objectStoreNames.contains('cotacoes')) {
        const localCotacoes = await db.getAllFromIndex('cotacoes', 'ean', cleanEan).catch(() => []);
        for (const c of (localCotacoes || [])) {
          const key = `${c.mercado}_${c.preco}`;
          if (!seenIds.has(c.id) && !seenQuotes.has(key)) {
            seenIds.add(c.id);
            seenQuotes.add(key);
            results.push(c);
          }
        }
      }

      // Complement with prod.historicoPrecos from 'produtos' store
      if (db.objectStoreNames.contains('produtos')) {
        const prod = await db.get('produtos', cleanEan).catch(() => null);
        if (prod && Array.isArray(prod.historicoPrecos)) {
          prod.historicoPrecos.forEach((h, idx) => {
            const hPrice = typeof h.preco === 'number' ? h.preco : (parseFloat(h.preco) || 0);
            const hMercado = h.local || prod.ultimoMercado || 'Não informado';
            const key = `${hMercado}_${hPrice}`;
            const fakeId = `hist_${cleanEan}_${idx}_${hPrice}`;

            if (!seenIds.has(fakeId) && !seenQuotes.has(key) && hPrice > 0) {
              seenIds.add(fakeId);
              seenQuotes.add(key);
              results.push({
                id: fakeId,
                ean: cleanEan,
                descricao: prod.descricao || 'Produto escaneado',
                mercado: hMercado,
                preco: hPrice,
                tipoFonte: 'scan_gondola',
                statusEstabelecimento: (hMercado && hMercado !== 'Não informado') ? 'definido' : 'pendente',
                userName: 'Você',
                createdAt: h.data || prod.scannedAt || new Date().toISOString()
              });
            }
          });
        }
      }
    } catch (e) {
      console.warn('[getCotacoesByEAN] IndexedDB lookup warning:', e);
    }
  }

  // 2. Fetch from backend API ONLY if explicitly requested or if local has 0 quotes and remote fetch allowed
  if (fetchRemote && authManager.isAuthenticated()) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const res = await authManager.fetchWithAuth(`/api/cotacoes/por-ean?ean=${encodeURIComponent(cleanEan)}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        const remoteQuotes = data.cotacoes || [];
        for (const c of remoteQuotes) {
          const key = `${c.mercado}_${c.preco}`;
          if (!seenIds.has(c.id) && !seenQuotes.has(key)) {
            seenIds.add(c.id);
            seenQuotes.add(key);
            results.push(c);
          }
        }
        if (remoteQuotes.length > 0) {
          saveCotacoesLocal(remoteQuotes).catch(() => {});
        }
      }
    } catch (e) {
      // Ignore network timeout gracefully
    }
  }

  return results.sort((a, b) => a.preco - b.preco);
}

/**
 * Retrieves recent price quotes for the live regional radar
 */
export async function getRecentCotacoes(limit = 20) {
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/cotacoes/recentes?limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        return data.cotacoes || [];
      }
    } catch (e) {
      console.warn('[getRecentCotacoes] Network fetch error:', e);
    }
  }

  try {
    const db = await getDB();
    if (db.objectStoreNames.contains('cotacoes')) {
      const all = await db.getAll('cotacoes');
      return (all || []).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, limit);
    }
  } catch (e) {
    return [];
  }
  return [];
}

/**
 * Retrieves quotes with pending store assignments
 */
export async function getPendingCotacoes(limit = 50) {
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth(`/api/cotacoes/pendentes?limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        return data.pendentes || [];
      }
    } catch (e) {
      console.warn('[getPendingCotacoes] Network error:', e);
    }
  }
  return [];
}

/**
 * Resolves pending quotes with a specified store in batch
 */
export async function resolvePendingCotacoes(ids, mercado) {
  if (!ids || ids.length === 0 || !mercado) return { resolved: 0 };
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth('/api/cotacoes/pendentes/resolver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, mercado })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      console.warn('[resolvePendingCotacoes] error:', e);
    }
  }
  return { resolved: 0 };
}

/**
 * Calls backend optimization for shopping lists
 */
export async function optimizeShoppingListBackend(itens) {
  if (!itens || itens.length === 0) return { ranking: [] };
  if (authManager.isAuthenticated()) {
    try {
      const res = await authManager.fetchWithAuth('/api/cotacoes/otimizar-lista', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itens })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      console.warn('[optimizeShoppingListBackend] error:', e);
    }
  }
  return { ranking: [] };
}

/**
 * Registers a price quote for an item and market, saving locally and syncing to backend
 */
export async function registerItemCotacao({ listId = null, itemId = null, ean = '', descricao, mercado, preco, precoOriginal = null, cidadeUF = '' }) {
  if (!mercado || typeof preco !== 'number' || preco <= 0) {
    throw new Error('Mercado e preço são obrigatórios');
  }

  const cleanEan = String(ean || (itemId ? `item_${itemId}` : '')).trim();
  const cleanMercado = String(mercado).trim();
  const cotacaoPayload = {
    id: `cot_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    listId: listId ? Number(listId) : null,
    itemId: itemId ? Number(itemId) : null,
    ean: cleanEan,
    descricao: String(descricao || '').trim(),
    mercado: cleanMercado,
    preco,
    precoOriginal,
    tipoFonte: 'scan_gondola',
    statusEstabelecimento: 'definido',
    cidadeUF,
    createdAt: new Date().toISOString()
  };

  // 1. Save in Backend
  if (authManager.isAuthenticated()) {
    try {
      await authManager.fetchWithAuth('/api/cotacoes/registrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cotacaoPayload)
      });
    } catch (e) {
      console.warn('[registerItemCotacao] Backend sync error:', e);
    }
  }

  // 2. Save in IndexedDB 'cotacoes' store
  if (typeof indexedDB !== 'undefined') {
    try {
      const db = await getDB();
      if (db.objectStoreNames.contains('cotacoes')) {
        await db.put('cotacoes', cotacaoPayload);
      }
      // Also record in 'produtos' price history if real product exists
      if (cleanEan && !cleanEan.startsWith('item_') && db.objectStoreNames.contains('produtos')) {
        const prod = await db.get('produtos', cleanEan).catch(() => null);
        if (prod) {
          const hist = Array.isArray(prod.historicoPrecos) ? [...prod.historicoPrecos] : [];
          hist.push({
            preco,
            data: new Date().toISOString(),
            local: cleanMercado
          });
          if (hist.length > 20) hist.shift();
          await db.put('produtos', {
            ...prod,
            ultimoPrecoPago: preco,
            ultimoMercado: cleanMercado,
            historicoPrecos: hist
          });
        }
      }
    } catch (e) {
      console.warn('[registerItemCotacao] Local DB error:', e);
    }
  }

  return cotacaoPayload;
}

/**
 * Saves multiple quotes into local IndexedDB
 */
export async function saveCotacoesLocal(cotacoes = []) {
  if (typeof indexedDB === 'undefined' || !Array.isArray(cotacoes) || cotacoes.length === 0) return;
  try {
    const db = await getDB();
    if (db.objectStoreNames.contains('cotacoes')) {
      const tx = db.transaction('cotacoes', 'readwrite');
      for (const c of cotacoes) {
        if (c && (c.id || c.ean)) {
          if (!c.id) c.id = `cot_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          c.synced = true; // Quotes coming from backend or batch are marked synced
          await tx.store.put(c);
        }
      }
      await tx.done;
    }
  } catch (e) {
    console.warn('[saveCotacoesLocal] error:', e);
  }
}

/**
 * Pushes any un-synced local price quotes from IndexedDB to backend server in background
 */
export async function syncLocalCotacoesToBackend() {
  if (typeof indexedDB === 'undefined' || !authManager.isAuthenticated()) return;
  try {
    const db = await getDB();
    if (!db.objectStoreNames.contains('cotacoes')) return;

    const allCotacoes = await db.getAll('cotacoes').catch(() => []);
    if (!Array.isArray(allCotacoes) || allCotacoes.length === 0) return;

    // Only upload quotes that have NOT been synced yet
    const pendingQuotes = allCotacoes.filter(c => !c.synced && c.mercado && Number(c.preco) > 0);
    if (pendingQuotes.length === 0) return;

    // Sync in batches of 5
    for (let i = 0; i < pendingQuotes.length; i += 5) {
      const batch = pendingQuotes.slice(i, i + 5);
      await Promise.all(batch.map(async (c) => {
        const cleanEan = String(c.ean || (c.itemId ? `item_${c.itemId}` : '')).trim();
        if (!cleanEan) return;
        try {
          const res = await authManager.fetchWithAuth('/api/cotacoes/registrar', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: c.id || `cot_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
              ean: cleanEan,
              descricao: String(c.descricao || c.description || 'Produto cotado').trim(),
              mercado: String(c.mercado).trim(),
              preco: Number(c.preco),
              preco_original: c.precoOriginal || c.preco_original || null,
              tipo_fonte: c.tipoFonte || c.tipo_fonte || 'scan_gondola',
              status_estabelecimento: c.statusEstabelecimento || c.status_estabelecimento || 'definido',
              cidade_uf: c.cidadeUF || c.cidade_uf || ''
            })
          });
          if (res.ok) {
            c.synced = true;
            await db.put('cotacoes', c).catch(() => {});
          }
        } catch (err) {
          console.warn('[syncLocalCotacoesToBackend] Item upload error:', err);
        }
      }));
    }
  } catch (e) {
    console.warn('[syncLocalCotacoesToBackend] error:', e);
  }
}

/**
 * Calculates multi-store price matrix, rankings, and mixed basket savings for a list of items
 * (Ultra-fast single-pass memory lookup from IndexedDB - 0 network calls)
 */
export async function getMultiStoreQuotesForList(items = []) {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      stores: [],
      itemsMatrix: [],
      ranking: [],
      mixedBasket: { total: 0, savings: 0, items: [] }
    };
  }

  // Fast single-pass reading from local IndexedDB
  const quotesByEan = new Map();
  const quotesByItemKey = new Map();

  if (typeof indexedDB !== 'undefined') {
    try {
      const db = await getDB();
      if (db.objectStoreNames.contains('cotacoes')) {
        const allCotacoes = await db.getAll('cotacoes').catch(() => []);
        for (const c of (allCotacoes || [])) {
          if (!c || !c.preco || c.preco <= 0) continue;
          const eanKey = String(c.ean || '').trim();
          if (eanKey) {
            if (!quotesByEan.has(eanKey)) quotesByEan.set(eanKey, []);
            quotesByEan.get(eanKey).push(c);
            if (eanKey.startsWith('item_')) {
              if (!quotesByItemKey.has(eanKey)) quotesByItemKey.set(eanKey, []);
              quotesByItemKey.get(eanKey).push(c);
            }
          }
          if (c.itemId) {
            const itemKey = `item_${c.itemId}`;
            if (!quotesByItemKey.has(itemKey)) quotesByItemKey.set(itemKey, []);
            quotesByItemKey.get(itemKey).push(c);
          }
        }
      }

      if (db.objectStoreNames.contains('produtos')) {
        const allProdutos = await db.getAll('produtos').catch(() => []);
        for (const prod of (allProdutos || [])) {
          const eanKey = String(prod.ean || prod.barcode || '').trim();
          if (eanKey && Array.isArray(prod.historicoPrecos)) {
            prod.historicoPrecos.forEach((h, idx) => {
              const hPrice = typeof h.preco === 'number' ? h.preco : (parseFloat(h.preco) || 0);
              const hMercado = h.local || prod.ultimoMercado || 'Não informado';
              if (hPrice > 0) {
                if (!quotesByEan.has(eanKey)) quotesByEan.set(eanKey, []);
                quotesByEan.get(eanKey).push({
                  id: `hist_${eanKey}_${idx}_${hPrice}`,
                  ean: eanKey,
                  mercado: hMercado,
                  preco: hPrice,
                });
              }
            });
          }
        }
      }
    } catch (e) {
      console.warn('[getMultiStoreQuotesForList] local quote load error:', e);
    }
  }

  const itemsMatrix = [];
  const storeSet = new Set();
  const storeTotals = new Map();

  for (const item of items) {
    const ean = (item.barcode || item.ean || '').trim();
    const qty = item.quantity || item.quantidade || 1;
    const estPrice = item.estimatedPrice || item.precoEstimado || 0;
    
    // In-memory quote lookup (0 network latency)
    const eanQuotes = ean ? (quotesByEan.get(ean) || []) : [];
    const itemKeyQuotes = item.id ? (quotesByItemKey.get(`item_${item.id}`) || []) : [];
    const allItemQuotes = [...eanQuotes, ...itemKeyQuotes];

    const pricesByStore = {};
    let lowestPrice = Infinity;
    let bestStore = '';

    for (const q of allItemQuotes) {
      const storeName = (q.mercado || '').trim();
      const p = typeof q.preco === 'number' ? q.preco : (parseFloat(q.preco) || 0);
      if (storeName && p > 0) {
        storeSet.add(storeName);
        if (!pricesByStore[storeName] || p < pricesByStore[storeName]) {
          pricesByStore[storeName] = p;
        }
        if (p < lowestPrice) {
          lowestPrice = p;
          bestStore = storeName;
        }
      }
    }

    itemsMatrix.push({
      item,
      ean,
      quantity: qty,
      estimatedPrice: estPrice,
      pricesByStore,
      lowestPrice: lowestPrice < Infinity ? lowestPrice : estPrice,
      bestStore: lowestPrice < Infinity ? bestStore : ''
    });
  }

  const stores = Array.from(storeSet);

  // Calculate totals and coverage per store
  for (const store of stores) {
    let total = 0;
    let count = 0;
    const storeItemDetails = [];

    for (const row of itemsMatrix) {
      const price = row.pricesByStore[store];
      if (typeof price === 'number' && price > 0) {
        const itemTotal = price * row.quantity;
        total += itemTotal;
        count++;
        storeItemDetails.push({
          item: row.item,
          unitPrice: price,
          totalPrice: itemTotal,
          available: true
        });
      } else {
        const fallback = (row.estimatedPrice > 0 ? row.estimatedPrice : (row.lowestPrice > 0 ? row.lowestPrice : 0)) * row.quantity;
        total += fallback;
        storeItemDetails.push({
          item: row.item,
          unitPrice: row.estimatedPrice || 0,
          totalPrice: fallback,
          available: false
        });
      }
    }

    storeTotals.set(store, {
      storeName: store,
      total,
      itemsQuoted: count,
      totalItems: itemsMatrix.length,
      coveragePercent: itemsMatrix.length > 0 ? Math.round((count / itemsMatrix.length) * 100) : 0,
      items: storeItemDetails
    });
  }

  // Ranking from cheapest to most expensive store
  const ranking = Array.from(storeTotals.values()).sort((a, b) => a.total - b.total);

  // Calculate Mixed Basket (buying each item where it is cheapest)
  let mixedTotal = 0;
  let singleStoreBestTotal = ranking[0]?.total || 0;
  const mixedItems = [];

  for (const row of itemsMatrix) {
    const unitPrice = row.lowestPrice;
    const itemTotal = unitPrice * row.quantity;
    mixedTotal += itemTotal;
    mixedItems.push({
      item: row.item,
      bestStore: row.bestStore || 'Estimativa Geral',
      unitPrice,
      totalPrice: itemTotal
    });
  }

  const savings = singleStoreBestTotal > mixedTotal ? singleStoreBestTotal - mixedTotal : 0;

  return {
    stores,
    itemsMatrix,
    ranking,
    mixedBasket: {
      total: mixedTotal,
      savings,
      singleBestTotal: singleStoreBestTotal,
      items: mixedItems
    }
  };
}

/**
 * Returns a Set of listIds that have recorded quotes in IndexedDB
 */
export async function getListsWithQuotesMap() {
  const set = new Set();
  if (typeof indexedDB === 'undefined') return set;
  try {
    const db = await getDB();
    if (db.objectStoreNames.contains('cotacoes')) {
      const allCotacoes = await db.getAll('cotacoes');
      for (const c of allCotacoes) {
        if (c.listId) set.add(Number(c.listId));
      }
    }
  } catch (e) {
    console.warn('Error fetching quotes map:', e);
  }
  return set;
}


// --- PRODUCT DICTIONARY & CATEGORY OVERRIDES ---

export async function getProductOverride(key) {
  try {
    const db = await initDB();
    return await db.get('product_dictionary', key);
  } catch (e) {
    console.error('Error fetching product override:', e);
    return null;
  }
}

export async function saveProductOverride(overridePayload) {
  // payload: { key: string, ean: string, normalizedName: string, categoryId: number, categoryName: string, updatedAt: timestamp }
  try {
    const db = await initDB();
    await db.put('product_dictionary', overridePayload);
  } catch (e) {
    console.error('Error saving product override:', e);
  }
}
